
import React from 'react';
import { motion } from 'framer-motion';
import { useProfile } from '@/hooks/useProfile';
import { useIsMobile } from '@/hooks/use-mobile';

interface PageHeaderProps {
  title: string;
  description?: string;
}

const PageHeader = ({ title, description }: PageHeaderProps) => {
  const { profile } = useProfile();
  const isMobile = useIsMobile();

  const fullName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';
  const welcomeMessage = fullName ? `¡Bienvenido ${fullName}!` : '¡Bienvenido!';

  return (
    <header className="relative bg-primary text-white py-12 md:py-20 overflow-hidden">
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 brightness-110"
        style={{
          backgroundImage: 'url("/lovable-uploads/e2e39701-504e-4674-b171-f52185261c7a.png")',
        }}
      />
      
      {/* Warm Gradient Overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-primary/30 to-primary/60 mix-blend-multiply"
        aria-hidden="true"
      />
      
      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 mt-4 md:mt-8">
        <div className="flex flex-col items-center gap-4 md:gap-8">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className={`text-2xl ${isMobile ? 'text-center px-2' : 'text-4xl'} font-bold text-center [text-shadow:_0_1px_2px_rgba(0,0,0,0.3)]`}
          >
            {title}
          </motion.h1>
          
          {description && (
            <motion.p 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center mt-2 max-w-2xl mx-auto text-primary-foreground/90 text-sm md:text-base px-4 [text-shadow:_0_1px_1px_rgba(0,0,0,0.2)]"
            >
              {description}
            </motion.p>
          )}
          
          {/* Welcome Message */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-lg md:text-2xl font-medium text-primary-foreground/90 [text-shadow:_0_1px_1px_rgba(0,0,0,0.2)]"
          >
            {welcomeMessage}
          </motion.p>
        </div>
      </div>
    </header>
  );
};

export default PageHeader;
